import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Button, TableCell, TableRow, TableContainer, Table, TableHead, TableBody, Dialog, DialogActions, FormControlLabel, Checkbox, FormGroup, DialogContent, DialogTitle } from '@mui/material';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { CountZoneTypeResponse } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../hooks/useLogError';
import { StockCountCreateContext, StockCountCreateValues } from '../../contexts/StockCountCreateContext';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';

const StockCountCreateCountZoneTypesModal = () => {
  const { isCountZoneTypesModalOpen,
    handleClose,
    countZoneTypesData,
    handleCountZoneTypes } = useContext(StockCountCreateContext) as StockCountCreateValues;

  const { selectedStore } = useContext(UserContext);
  const { getCountZoneTypes } = useNswagClient();
  const [data, setData] = useState<CountZoneTypeResponse[] | undefined>([]);
  const [tempCountZoneTypesData, setTempCountZoneTypesData] = useState<CountZoneTypeResponse[] | undefined>([]);
  const { t } = useTranslation('createStockCount');
  const { logError } = useLogError();

  const handleConfirm = () => {
    handleCountZoneTypes(tempCountZoneTypesData || []);
    setTempCountZoneTypesData([]);
  };

  const handleCountZoneTypeCheckboxChange = (countZoneTypeId: number, isChecked: boolean) => {
    if (isChecked) {
      const selected = data?.find(row => row.id === countZoneTypeId);
      if (selected && !tempCountZoneTypesData?.some(z => z.id === countZoneTypeId)) {
        setTempCountZoneTypesData(prev => [...(prev ?? []), selected]);
      }
    } else {
      setTempCountZoneTypesData(prev => (prev ?? []).filter(row => row.id !== countZoneTypeId));
    }
  };


  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTempCountZoneTypesData(checked ? data ?? [] : []);
  };

  useEffect(() => {
    if (isCountZoneTypesModalOpen) {
      setTempCountZoneTypesData(countZoneTypesData ?? []);
    }
    getData();
  }, [isCountZoneTypesModalOpen]);

  const getData = () => {
    const storeNumber = selectedStore?.storeNumber === undefined ? '' : selectedStore.storeNumber;
    getCountZoneTypes(storeNumber).then((result) => {
      if (result) {
        const countZoneTypes = result ?? new Array<CountZoneTypeResponse>();
        countZoneTypes.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
        setData(countZoneTypes);
      } else {
        logError(result);
      }
    })
      .catch((error) => {
        logError(error);
      });
  };

  return (
    <Dialog open={isCountZoneTypesModalOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('countZoneTypes.title')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ overflowy: 'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell >{t('countZoneTypes.name')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}  >
                    <Checkbox
                      color="primary"
                      inputProps={{
                        'aria-label': `select count zone type ${row.name}`,
                      }}
                      checked={tempCountZoneTypesData?.some(item => item.id === row.id)}
                      onChange={(e) => handleCountZoneTypeCheckboxChange(row.id!, e.target.checked)} />
                    {row.name}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup sx={{ pt: '10px' }}>
          <FormControlLabel
            control={<Checkbox onChange={handleSelectAllChange} />}
            label={t('countZoneTypes.selectAll')} />
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <Button
          variant="secondary"
          size='lg'
          onClick={handleClose}> {t('cancel')}</Button>
        <Button
          size='lg'
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>

  );
};

export default StockCountCreateCountZoneTypesModal;
